import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  applyStoredConsent,
  isSensitiveRoute,
  setConsent,
  shouldAskForConsent,
} from "@/lib/analytics";

/**
 * Asks before session replay is switched on, and applies a previous decision.
 *
 * Replay used to start automatically for every visitor. This gives hosts a real
 * choice, and guests are never asked at all: replay stays off on /i/ routes
 * because those pages show other people's personal details (see lib/analytics).
 */
export default function ReplayConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Re-evaluated per navigation so entering a guest route stops any recording
    // that a host session had started.
    applyStoredConsent(location.pathname);
    setVisible(shouldAskForConsent(location.pathname));
  }, [location.pathname]);

  if (!visible || isSensitiveRoute(location.pathname)) return null;

  const decide = (value) => {
    setConsent(value);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="replay-consent-title"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 bg-card border border-border rounded-2xl shadow-2xl p-5"
    >
      <h2 id="replay-consent-title" className="font-display text-lg font-medium mb-1">
        Help us improve WeGathered?
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        We can record how you use the editor to find bugs and rough edges. Typed
        text is masked, and we never record invitation pages your guests open. You
        can change this any time.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => decide(CONSENT_GRANTED)}
          data-testid="replay-consent-accept"
          className="flex-1 bg-primary text-primary-foreground rounded-full py-2.5 text-sm font-medium hover:scale-[1.02] transition-transform"
        >
          Allow
        </button>
        <button
          type="button"
          onClick={() => decide(CONSENT_DENIED)}
          data-testid="replay-consent-decline"
          className="flex-1 border border-border rounded-full py-2.5 text-sm font-medium hover:bg-muted transition-colors"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}
